import { useCallback, useState } from 'react'
import {
  Background,
  type EdgeTypes,
  type NodeChange,
  Panel,
  ReactFlow,
  ReactFlowInstance,
  SelectionMode,
  useReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useShallow } from 'zustand/shallow'
import { useFlowStore } from '@/stores/flow-store'
import { cn } from '@/lib/utils'
import { useAddNodeOnEdgeDrop } from '@/hooks/use-add-node-on-edge-drop'
import { useDeleteKeyCode } from '@/hooks/use-delete-key-code'
import { useDragDropFlowBuilder } from '@/hooks/use-drag-drop-flow-builder'
import { useIsValidConnection } from '@/hooks/use-is-valid-connection'
import { useNodeAutoAdjust } from '@/hooks/use-node-auto-adjust'
import { Card } from '@/components/ui/card'
import AddNodeFloatingMenu from './components/add-node-floating-menu/add-node-floating-menu'
import { NODE_TYPES } from './components/blocks'
import { SidebarModule } from './components/blocks/sidebar/sidebar-module'
import { BuilderNode } from './components/blocks/types'
import { FlowContextMenu } from './components/context-menu/flow-context-menu'
import CustomControls from './components/controls/custom-controls'
import CustomDeletableEdge from './components/edges/custom-deletable-edge'
import { SaveFlowButton } from './components/ui/save-flow-buttom'

const edgeTypes: EdgeTypes = {
  deletable: CustomDeletableEdge,
}

export const FlowBuilder = () => {
  const [
    name,
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    deleteNode,
    deleteEdge,
  ] = useFlowStore(
    useShallow((s) => [
      s.workflow.name,
      s.workflow.nodes,
      s.workflow.edges,
      s.actions.nodes.onNodesChange,
      s.actions.edges.onEdgesChange,
      s.actions.edges.onConnect,
      s.actions.nodes.deleteNode,
      s.actions.edges.deleteEdge,
    ])
  )
  const { getNodes } = useReactFlow()
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null)

  const {
    handleOnEdgeDropConnectEnd,
    floatingMenuWrapperRef,
    handleAddConnectedNode,
  } = useAddNodeOnEdgeDrop()

  const deleteKeyCode = useDeleteKeyCode()
  // const onNodesDelete = useOnNodesDelete(nodes);

  const [onDragOver, onDrop] = useDragDropFlowBuilder()
  const isValidConnection = useIsValidConnection(nodes, edges)

  const autoAdjustNode = useNodeAutoAdjust()

  const handleAutoAdjustNodeAfterNodeMeasured = useCallback(
    (id: string) => {
      setTimeout(() => {
        const node = getNodes().find((n) => n.id === id)
        if (!node) {
          return
        }

        if (node.measured === undefined) {
          handleAutoAdjustNodeAfterNodeMeasured(id)
          return
        }

        autoAdjustNode(node)
      })
    },
    [autoAdjustNode, getNodes]
  )

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const filteredChanges = changes.map((change) => {
        if (change.type === 'select') {
          const node = nodes.find((n) => n.id === change.id)
          if (
            node &&
            (node.type === BuilderNode.START || node.type === BuilderNode.END)
          ) {
            return { ...change, selected: false }
          }
        }
        return change
      })

      filteredChanges.forEach((change) => {
        if (change.type === 'dimensions') {
          const node = getNodes().find((n) => n.id === change.id)
          if (node) {
            autoAdjustNode(node)
          }
        }

        if (change.type === 'add') {
          handleAutoAdjustNodeAfterNodeMeasured(change.item.id)
        }
      })
      onNodesChange(filteredChanges)
    },
    [
      autoAdjustNode,
      getNodes,
      handleAutoAdjustNodeAfterNodeMeasured,
      onNodesChange,
      nodes,
    ]
  )

  const handleDeleteElements = useCallback(() => {
    const selectedNodes = nodes.filter(
      (node) =>
        node.selected &&
        node.type !== BuilderNode.START &&
        node.type !== BuilderNode.END
    )
    const selectedEdges = edges.filter((edge) => edge.selected)

    selectedNodes.forEach((node) => deleteNode(node))
    selectedEdges.forEach((edge) => deleteEdge(edge))
  }, [nodes, edges, deleteNode, deleteEdge])

  return (
    <>
      <Card className='flex h-14 w-full items-center justify-between border-0 border-b border-card-foreground/10 to-transparent p-2'>
        <div className='ml-4 inline-flex items-center'>
          <h1 className='font-bold text-card-foreground'>{name}</h1>
        </div>

        {name !== '' && (
          <div className='inline-flex items-center gap-2'>
            <SaveFlowButton flowInstance={reactFlowInstance} />
          </div>
        )}
      </Card>
      <div className='relative h-[94vh] w-full'>
        <FlowContextMenu>
          <ReactFlow
            proOptions={{ hideAttribution: true }}
            onInit={(instance) =>
              instance.fitView().then(() => setReactFlowInstance(instance))
            }
            nodeTypes={NODE_TYPES}
            nodes={nodes}
            onNodesChange={handleNodesChange}
            edgeTypes={edgeTypes}
            edges={edges}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectEnd={handleOnEdgeDropConnectEnd}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeDragStop={(_, node) => {
              autoAdjustNode(node)
            }}
            onNodesDelete={handleDeleteElements}
            isValidConnection={isValidConnection}
            selectionMode={SelectionMode.Full}
            multiSelectionKeyCode='Control'
            selectionOnDrag={true}
            selectionKeyCode={null}
            deleteKeyCode={deleteKeyCode}
            snapGrid={[16, 16]}
            snapToGrid
            fitView
          >
            <Background gap={24} />
            <CustomControls />
            <Panel
              className='h-[520px] overflow-hidden rounded-lg border border-card-foreground/10'
              position='top-left'
            >
              <SidebarModule />
            </Panel>
          </ReactFlow>
        </FlowContextMenu>
      </div>
      <div
        className={cn(
          'pointer-events-none absolute inset-0 backdrop-blur-3xl transition-all',
          'pointer-events-none bg-zinc-800/0 opacity-0 backdrop-saturate-100'
        )}
      >
        <div ref={floatingMenuWrapperRef} className='relative size-full'>
          <AddNodeFloatingMenu onNodeAdd={handleAddConnectedNode} />
        </div>
      </div>
    </>
  )
}
